import { SaveData, MAX_PLAYER_LEVEL } from "./Save";
import { CAMPAIGN_MAPS } from "../maps/maps";
import { TOWERS, ENEMIES } from "../config";

export type AchievementCategory = "campaign" | "mastery" | "slayer" | "challenge" | "meta";
export type AchievementTier = "bronze" | "silver" | "gold" | "platinum";

export interface RunStats {
  mapKey: string;
  difficulty: number;
  victory: boolean;
  leaks: number;
  durationMs: number;
  uniqueTowerTypes: number;
  towersOnField: number;
  onlyTowerKey: string | null;
}

export type AchievementEvent =
  | { kind: "wave";       waveNumber: number; difficultyMult: number }
  | { kind: "runWon";     mapKey: string; difficulty: number }
  | { kind: "runEnd";     stats: RunStats }
  | { kind: "kill";       enemyKey: string; isBoss: boolean }
  | { kind: "built";      towerKey: string }
  | { kind: "level";      playerLevel: number }
  | { kind: "skillSpent"; total: number };

export interface AchievementReward {
  xp?: number;
  skillPoints?: number;
}

export interface AchievementDef {
  id: string;
  category: AchievementCategory;
  tier: AchievementTier;
  name: string;
  desc: string;
  hidden?: boolean;
  /** Target value for progress bar display. */
  target?: number;
  check: (e: AchievementEvent, save: SaveData, progress: number) => number | true | null;
  reward: AchievementReward;
}

/** Default rewards by tier — def.reward fields override these. */
export const TIER_DEFAULTS: Record<AchievementTier, AchievementReward> = {
  bronze:   { xp: 100 },
  silver:   { xp: 500 },
  gold:     { xp: 1500, skillPoints: 1 },
  platinum: { xp: 3000, skillPoints: 1 },
};

// ────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────────────────────

function sumRecord(r: Record<string, number>): number {
  let t = 0;
  for (const k of Object.keys(r)) t += r[k];
  return t;
}

function countSeen(r: Record<string, number>): number {
  let n = 0;
  for (const k of Object.keys(r)) if (r[k] > 0) n++;
  return n;
}

function sumSpentSkillPoints(save: SaveData): number {
  let total = 0;
  for (const k of Object.keys(save.spentSkillPoints)) total += save.spentSkillPoints[k];
  return total;
}

/** Title-case a snake_case key for display: "armored_orc" -> "Armored Orc". */
function titleCase(key: string): string {
  return key.split("_").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
}

const DIFFICULTY_NAME: Record<number, string> = { 1: "Squire", 2: "Knight", 3: "Lord" };

// ────────────────────────────────────────────────────────────────────────────
// Definitions
// ────────────────────────────────────────────────────────────────────────────
const defs: AchievementDef[] = [];

// ── Campaign ───────────────────────────────────────────────────────────────

const CAMPAIGN_TIER: Record<number, AchievementTier> = { 1: "bronze", 2: "silver", 3: "gold" };

defs.push({
  id: "camp.first.win",
  category: "campaign", tier: "bronze",
  name: "Baptism of Steel",
  desc: "Win your first run on any map.",
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory ? true : null;
  },
  reward: {},
});

for (const map of CAMPAIGN_MAPS) {
  for (const diff of [1, 2, 3] as const) {
    defs.push({
      id: `camp.${map.key}.d${diff}`,
      category: "campaign", tier: CAMPAIGN_TIER[diff],
      name: `${map.name} — ${DIFFICULTY_NAME[diff]}`,
      desc: `Win ${map.name} on ${DIFFICULTY_NAME[diff]} difficulty.`,
      check: (_e, save) => (save.campaign.cleared[map.key] ?? 0) >= diff ? true : null,
      reward: {},
    });
  }
}

defs.push({
  id: "camp.all.squire",
  category: "campaign", tier: "silver",
  name: "Pathfinder",
  desc: "Win every campaign map on Squire difficulty.",
  check: (_e, save) => {
    for (const m of CAMPAIGN_MAPS) {
      if ((save.campaign.cleared[m.key] ?? 0) < 1) return null;
    }
    return true;
  },
  reward: {},
});

defs.push({
  id: "camp.all.knight",
  category: "campaign", tier: "gold",
  name: "Knight Errant",
  desc: "Win every campaign map on Knight difficulty.",
  check: (_e, save) => {
    for (const m of CAMPAIGN_MAPS) {
      if ((save.campaign.cleared[m.key] ?? 0) < 2) return null;
    }
    return true;
  },
  reward: {},
});

defs.push({
  id: "camp.all.lord",
  category: "campaign", tier: "platinum",
  name: "Crown of the Realm",
  desc: "Win every campaign map on Lord difficulty.",
  check: (_e, save) => {
    for (const m of CAMPAIGN_MAPS) {
      if ((save.campaign.cleared[m.key] ?? 0) < 3) return null;
    }
    return true;
  },
  reward: {},
});

defs.push({
  id: "camp.flawless.any",
  category: "campaign", tier: "gold",
  name: "Untouched",
  desc: "Win any campaign map without leaking a single enemy.",
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory && e.stats.leaks === 0 ? true : null;
  },
  reward: {},
});

// ── Mastery — per-tower build counts (cumulative across runs) ──────────────

interface MasteryTier { suffix: string; tier: AchievementTier; target: number; label: string }
const MASTERY_TIERS: MasteryTier[] = [
  { suffix: "apprentice", tier: "bronze", target: 10, label: "Apprentice" },
  { suffix: "master",     tier: "gold",   target: 50, label: "Master"     },
];

for (const t of Object.values(TOWERS)) {
  for (const tier of MASTERY_TIERS) {
    defs.push({
      id: `mastery.${t.key}.${tier.suffix}`,
      category: "mastery", tier: tier.tier,
      name: `${t.name} ${tier.label}`,
      desc: `Build ${tier.target} ${t.name} towers across all runs.`,
      target: tier.target,
      check: (_e, save) => {
        const n = save.codex.towersBuilt[t.key] ?? 0;
        return n >= tier.target ? true : n;
      },
      reward: {},
    });
  }
}

defs.push({
  id: "mastery.arsenal",
  category: "mastery", tier: "silver",
  name: "Arsenal",
  desc: "Build every tower type at least once.",
  target: Object.keys(TOWERS).length,
  check: (_e, save) => {
    let built = 0;
    for (const t of Object.values(TOWERS)) {
      if ((save.codex.towersBuilt[t.key] ?? 0) >= 1) built++;
    }
    return built >= Object.keys(TOWERS).length ? true : built;
  },
  reward: {},
});

defs.push({
  id: "mastery.architect",
  category: "mastery", tier: "platinum",
  name: "Master Architect",
  desc: "Build 500 towers across all runs.",
  target: 500,
  check: (_e, save) => {
    const n = sumRecord(save.codex.towersBuilt);
    return n >= 500 ? true : n;
  },
  reward: {},
});

// ── Slayer — kill counts for enemies actually used in waves ────────────────

interface SlayerTarget { key: string; target: number; tier: AchievementTier }
const SLAYER_TARGETS: SlayerTarget[] = [
  // Trash (high spawn count)
  { key: "goblin",          target: 200, tier: "bronze" },
  { key: "skeleton",        target: 200, tier: "bronze" },
  { key: "swarmling",       target: 250, tier: "bronze" },
  // Common threats
  { key: "orc",             target: 150, tier: "silver" },
  { key: "wraith",          target:  80, tier: "silver" },
  { key: "flame_imp",       target:  80, tier: "silver" },
  { key: "kobold_rider",    target:  60, tier: "silver" },
  // Heavy / specialists
  { key: "armored_orc",     target:  80, tier: "silver" },
  { key: "shieldman",       target:  60, tier: "silver" },
  { key: "bog_lurker",      target:  40, tier: "silver" },
  { key: "ice_elemental",   target:  40, tier: "silver" },
  { key: "desert_nomad",    target:  40, tier: "silver" },
  { key: "gargoyle",        target:  40, tier: "gold"   },
  { key: "revenant",        target:  40, tier: "gold"   },
  { key: "shadow_assassin", target:  40, tier: "gold"   },
  // Elites
  { key: "troll",           target:  30, tier: "gold"   },
  { key: "cart",            target:  20, tier: "gold"   },
  { key: "siege_ram",       target:  10, tier: "platinum" },
];

for (const s of SLAYER_TARGETS) {
  // Skip silently if a slayer entry references a removed/renamed enemy —
  // keeps the list valid even if config drifts.
  if (!ENEMIES[s.key]) continue;
  const display = titleCase(s.key);
  defs.push({
    id: `slay.${s.key}.${s.target}`,
    category: "slayer", tier: s.tier,
    name: `${display} Bane`,
    desc: `Kill ${s.target} ${display}s across all runs.`,
    target: s.target,
    check: (_e, save) => {
      const n = save.codex.enemiesKilled[s.key] ?? 0;
      return n >= s.target ? true : n;
    },
    reward: {},
  });
}

defs.push({
  id: "slay.horde.500",
  category: "slayer", tier: "silver",
  name: "One-Man Horde",
  desc: "Kill 500 enemies in total.",
  target: 500,
  check: (_e, save) => {
    const n = sumRecord(save.codex.enemiesKilled);
    return n >= 500 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "slay.horde.2500",
  category: "slayer", tier: "gold",
  name: "Legion's End",
  desc: "Kill 2,500 enemies in total.",
  target: 2500,
  check: (_e, save) => {
    const n = sumRecord(save.codex.enemiesKilled);
    return n >= 2500 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "slay.horde.10000",
  category: "slayer", tier: "platinum",
  name: "Reaper of Realms",
  desc: "Kill 10,000 enemies in total.",
  target: 10000,
  check: (_e, save) => {
    const n = sumRecord(save.codex.enemiesKilled);
    return n >= 10000 ? true : n;
  },
  reward: {},
});

// Bosses — one per boss + the sweep.
interface BossDef { key: string; name: string }
const BOSSES: BossDef[] = [
  { key: "boss_warlord",      name: "Warlord of the Pass" },
  { key: "boss_bridge_troll", name: "Bridge Troll"        },
  { key: "boss_frost_wyrm",   name: "Frost Wyrm"          },
];

for (const b of BOSSES) {
  if (!ENEMIES[b.key]) continue;
  defs.push({
    id: `slay.${b.key}`,
    category: "slayer", tier: "silver",
    name: `Slayer of the ${b.name}`,
    desc: `Defeat the ${b.name} boss.`,
    check: (_e, save) => (save.codex.bossesKilled[b.key] ?? 0) >= 1 ? true : null,
    reward: {},
  });
}

defs.push({
  id: "slay.boss.all",
  category: "slayer", tier: "gold",
  name: "Bane of the Mighty",
  desc: "Defeat every campaign boss at least once.",
  check: (_e, save) => {
    for (const b of BOSSES) {
      if (!ENEMIES[b.key]) continue;
      if ((save.codex.bossesKilled[b.key] ?? 0) < 1) return null;
    }
    return true;
  },
  reward: {},
});

// ── Challenge — single-run conditions ──────────────────────────────────────

defs.push({
  id: "chal.mono.archer",
  category: "challenge", tier: "gold",
  name: "Bowmen Only",
  desc: "Win any map using only Archer towers.",
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory && e.stats.onlyTowerKey === "archer" ? true : null;
  },
  reward: {},
});

defs.push({
  id: "chal.mono.cannon",
  category: "challenge", tier: "gold",
  name: "By Powder Alone",
  desc: "Win any map using only Cannon towers.",
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory && e.stats.onlyTowerKey === "cannon" ? true : null;
  },
  reward: {},
});

defs.push({
  id: "chal.mono.frost",
  category: "challenge", tier: "platinum",
  name: "Banner of the Glacier",
  desc: "Win any map using only Frost Spire towers.",
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory && e.stats.onlyTowerKey === "frost" ? true : null;
  },
  reward: {},
});

defs.push({
  id: "chal.frugal",
  category: "challenge", tier: "gold",
  name: "The Frugal Commander",
  desc: "Win any map with 6 or fewer towers on the field.",
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory && e.stats.towersOnField <= 6 ? true : null;
  },
  reward: {},
});

defs.push({
  id: "chal.diversified",
  category: "challenge", tier: "silver",
  name: "Diversified Defense",
  desc: "Win any map using at least 5 different tower types.",
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory && e.stats.uniqueTowerTypes >= 5 ? true : null;
  },
  reward: {},
});

// Speedruns — keys MUST match CAMPAIGN_MAPS keys exactly.
defs.push({
  id: "chal.speedrun.greenwold",
  category: "challenge", tier: "silver",
  name: "Swift Defender",
  desc: "Win Greenwold Pass in under 5 minutes.",
  hidden: true,
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory
      && e.stats.mapKey === "greenwold_pass"
      && e.stats.durationMs < 5 * 60 * 1000
      ? true : null;
  },
  reward: {},
});

defs.push({
  id: "chal.speedrun.stonebridge",
  category: "challenge", tier: "gold",
  name: "Bridgemaster",
  desc: "Win Stonebridge in under 7 minutes.",
  hidden: true,
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory
      && e.stats.mapKey === "stonebridge"
      && e.stats.durationMs < 7 * 60 * 1000
      ? true : null;
  },
  reward: {},
});

defs.push({
  id: "chal.lord.flawless",
  category: "challenge", tier: "platinum",
  name: "Ironclad Dominion",
  desc: "Win any map on Lord difficulty without leaking a single enemy.",
  hidden: true,
  check: (e) => {
    if (e.kind !== "runEnd") return null;
    return e.stats.victory && e.stats.difficulty >= 3 && e.stats.leaks === 0 ? true : null;
  },
  reward: {},
});

// ── Meta — long-term progression ───────────────────────────────────────────

defs.push({
  id: "meta.level.10",
  category: "meta", tier: "bronze",
  name: "Rising Squire",
  desc: "Reach player level 10.",
  target: 10,
  check: (_e, save) => save.playerLevel >= 10 ? true : save.playerLevel,
  reward: {},
});

defs.push({
  id: "meta.level.30",
  category: "meta", tier: "silver",
  name: "Seasoned Warden",
  desc: "Reach player level 30.",
  target: 30,
  check: (_e, save) => save.playerLevel >= 30 ? true : save.playerLevel,
  reward: {},
});

defs.push({
  id: "meta.level.60",
  category: "meta", tier: "platinum",
  name: "Veteran Commander",
  desc: `Reach the level cap (${MAX_PLAYER_LEVEL}).`,
  target: MAX_PLAYER_LEVEL,
  check: (_e, save) => save.playerLevel >= MAX_PLAYER_LEVEL ? true : save.playerLevel,
  reward: {},
});

defs.push({
  id: "meta.skill.5",
  category: "meta", tier: "bronze",
  name: "First Steps",
  desc: "Spend 5 total skill points.",
  target: 5,
  check: (_e, save) => {
    const n = sumSpentSkillPoints(save);
    return n >= 5 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "meta.skill.15",
  category: "meta", tier: "silver",
  name: "Skilled Tactician",
  desc: "Spend 15 total skill points.",
  target: 15,
  check: (_e, save) => {
    const n = sumSpentSkillPoints(save);
    return n >= 15 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "meta.skill.30",
  category: "meta", tier: "gold",
  name: "Grand Strategist",
  desc: "Spend 30 total skill points.",
  target: 30,
  check: (_e, save) => {
    const n = sumSpentSkillPoints(save);
    return n >= 30 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "meta.runs.10",
  category: "meta", tier: "bronze",
  name: "Veteran of Ten Battles",
  desc: "Complete 10 runs (any outcome).",
  target: 10,
  check: (_e, save) => {
    const n = save.stats.runsPlayed;
    return n >= 10 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "meta.runs.50",
  category: "meta", tier: "silver",
  name: "Lifelong Defender",
  desc: "Complete 50 runs (any outcome).",
  target: 50,
  check: (_e, save) => {
    const n = save.stats.runsPlayed;
    return n >= 50 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "meta.waves.100",
  category: "meta", tier: "silver",
  name: "Hundred Tides",
  desc: "Clear 100 waves across all runs.",
  target: 100,
  check: (_e, save) => {
    const n = save.stats.wavesCleared;
    return n >= 100 ? true : n;
  },
  reward: {},
});

defs.push({
  id: "meta.waves.500",
  category: "meta", tier: "gold",
  name: "Endless Vigil",
  desc: "Clear 500 waves across all runs.",
  target: 500,
  check: (_e, save) => {
    const n = save.stats.wavesCleared;
    return n >= 500 ? true : n;
  },
  reward: {},
});

// Bestiary — every non-boss enemy that actually appears in CAMPAIGN_MAPS waves.
const BESTIARY_KEYS: string[] = (() => {
  const keys = new Set<string>();
  for (const map of CAMPAIGN_MAPS) {
    for (const wave of map.waves) {
      for (const entry of wave.entries) {
        const e = ENEMIES[entry.type];
        if (e && !e.boss) keys.add(entry.type);
      }
    }
  }
  return [...keys];
})();

defs.push({
  id: "meta.bestiary",
  category: "meta", tier: "gold",
  name: "Bestiary",
  desc: `Encounter every non-boss enemy in the campaign (${BESTIARY_KEYS.length} types).`,
  target: BESTIARY_KEYS.length,
  check: (_e, save) => {
    let seen = 0;
    for (const k of BESTIARY_KEYS) {
      if ((save.codex.enemiesKilled[k] ?? 0) > 0) seen++;
    }
    return seen >= BESTIARY_KEYS.length ? true : seen;
  },
  reward: {},
});

defs.push({
  id: "meta.codex.full",
  category: "meta", tier: "platinum",
  name: "Loremaster",
  desc: "Encounter every enemy and build every tower in the game.",
  check: (_e, save) => {
    for (const t of Object.values(TOWERS)) {
      if ((save.codex.towersBuilt[t.key] ?? 0) < 1) return null;
    }
    for (const k of Object.keys(ENEMIES)) {
      const e = ENEMIES[k];
      if (e.boss) {
        if ((save.codex.bossesKilled[k] ?? 0) < 1) return null;
      } else {
        if ((save.codex.enemiesKilled[k] ?? 0) < 1) return null;
      }
    }
    return true;
  },
  reward: {},
});

defs.push({
  id: "meta.dedication",
  category: "meta", tier: "silver",
  name: "Dedication",
  desc: "Unlock 25 other achievements.",
  hidden: true,
  target: 25,
  check: (_e, save) => {
    const n = countSeen(save.achievements?.unlocked ?? {});
    return n >= 25 ? true : n;
  },
  reward: {},
});

// ────────────────────────────────────────────────────────────────────────────
// Export as a map keyed by id
// ────────────────────────────────────────────────────────────────────────────
export const ACHIEVEMENT_DEFS: Record<string, AchievementDef> = {};
for (const d of defs) ACHIEVEMENT_DEFS[d.id] = d;

/** Raw ordered list. The Record above silently collapses duplicate ids, so
 *  id uniqueness is only assertable from here (src/systems/defs.test.ts). */
export const ACHIEVEMENT_DEF_LIST: readonly AchievementDef[] = defs;
